// ActiveWarrantsBanner - pinned banner above OpenItemsSummary surfacing
// any active outstanding warrants on this person's record.
//
// Why this exists: a bench warrant is the single most time-sensitive
// problem on intake - a traffic stop becomes a custody event the moment
// the warrant exists, regardless of how minor the underlying offense is.
// We were already detecting warrants for the cross-person Today triage
// view; the per-person page should be at least as loud.
//
// What the banner does:
//   • Walks every record's summary + subPages for an "Outstanding-Y" /
//     "Active Warrant" flag via recordHasActiveWarrant from lib/openItems
//     (the single client-side source of truth - the server keeps its own
//     mirror in recordHasWarrantRaw; if behavior changes here, mirror there).
//   • Pulls the same warrant detail the Today page shows (offense code +
//     description, warrant issued date, bail, court phone/address, etc.)
//     directly from RecordRow.data - no extra API call needed.
//   • Groups by court so the warrant-recall letter (one-per-court) gets
//     a dedicated download button per jurisdiction.
//   • Each warrant case is click-to-expand for the full detail.
//   • Hides itself entirely when no record carries the flag.
//
// Placement: ABOVE LicenseSuspendedBanner in PersonPage. Warrants outrank
// license suspensions because driving with a held license is a ticket;
// driving with an active warrant is custody.

import { useMemo, useState } from "react";
import type { RecordRow, RecordData } from "../types";
import { recordHasActiveWarrant } from "../lib/openItems";

interface Props {
  jobId: string;
  records: RecordRow[];
}

function isRecordData(x: unknown): x is RecordData {
  return (
    !!x
    && typeof x === "object"
    && "summary" in (x as Record<string, unknown>)
    && typeof (x as { summary: unknown }).summary === "object"
  );
}

function pickStr(field: string | string[] | undefined): string {
  if (!field) return "";
  return Array.isArray(field) ? field.at(-1) ?? "" : field;
}

// recordHasActiveWarrant is imported from ../lib/openItems above -
// see the comment block at the top of this file for why.

interface WarrantCase {
  recordId: number;
  caseNumber: string;
  court: string;
  offenseCode: string;
  offenseDescription: string;
  warrantNumber: string;
  warrantIssuedDate: string;
  originalOffenseDate: string;
  bail: string;
  status: string;
  courtPhone: string;
  courtAddress: string;
  courtDateTime: string;
  courtRoom: string;
}

/** Title-case the court name to match the bucket key the warrant-letter
 *  route uses. Mirrors today.ts:detectActiveWarrants (which mirrors
 *  clientSummary.cleanCourtName). If this drifts, the per-court ?court=
 *  filter on the letter download silently picks the wrong court. */
function cleanCourt(raw: string): string {
  if (!raw) return "";
  return raw
    .replace(/^[\d\s-]+/, "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function extractWarrantCases(records: RecordRow[]): WarrantCase[] {
  const out: WarrantCase[] = [];
  for (const r of records) {
    if (!isRecordData(r.data)) continue;
    if (!recordHasActiveWarrant(r.data)) continue;
    const summary = r.data.summary;
    const subFields = r.data.subPages?.[0]?.fields ?? {};
    const offenseCode = pickStr(summary["Offense"]);
    let offenseDescription = pickStr(summary["Offense Description"]);
    if (!offenseDescription) {
      const combined = pickStr(subFields["Offense"]);
      offenseDescription = combined.replace(/^[\d:.\-\s]+-\s*/, "").trim();
    }
    out.push({
      recordId: r.id,
      caseNumber:
        (r.case_number ?? "")
        || pickStr(summary["Prefix/Year/Number"])
        || pickStr(summary["Prefix/Number"])
        || pickStr(summary["Case"])
        || "",
      court: cleanCourt(pickStr(summary["Court"])),
      offenseCode,
      offenseDescription,
      warrantNumber: pickStr(summary["Warrant Number"]),
      warrantIssuedDate: pickStr(summary["Warrant Issued Date"]),
      originalOffenseDate:
        pickStr(subFields["Offense Date"])
        || pickStr(subFields["Issue Date"]),
      bail: pickStr(subFields["Bail"]),
      status: pickStr(summary["Status"]),
      courtPhone: pickStr(summary["Court Phone"]),
      courtAddress: pickStr(summary["Address"]),
      courtDateTime: pickStr(summary["Court Date/Time"]),
      courtRoom: pickStr(summary["Court Room"]),
    });
  }
  return out;
}

export function ActiveWarrantsBanner({ jobId, records }: Props): JSX.Element | null {
  const cases = useMemo(() => extractWarrantCases(records), [records]);

  // Group by court so the warrant-recall letter (one-letter-per-court)
  // gets one download button per jurisdiction. NOTE: this hook MUST stay
  // above the early-return below - placing useMemo after a conditional
  // `return null` violates the Rules of Hooks (the hook count must be
  // identical across every render of the same component).
  const byCourt = useMemo(() => {
    const m = new Map<string, WarrantCase[]>();
    for (const c of cases) {
      const key = c.court || "(unknown court)";
      const arr = m.get(key) ?? [];
      arr.push(c);
      m.set(key, arr);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [cases]);

  if (cases.length === 0) return null;

  return (
    <section className="container station station-flush-top">
      <div
        className="card-elevated dark-tile overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #C13455 0%, #7A1F3A 100%)",
          color: "#fff",
          border: "none",
          boxShadow: "0 18px 36px -12px rgba(193, 52, 85, 0.45)",
        }}
      >
        <div className="px-5 py-4 sm:px-6 sm:py-5">
          {/* Eyebrow + count */}
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="flex items-baseline gap-3">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  fontSize: 18,
                  fontWeight: 900,
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: 4,
                  padding: "1px 8px",
                  letterSpacing: "0.04em",
                  fontFamily: "var(--font-mono)",
                }}
              >
                ⚠
              </span>
              <div
                className="eyebrow text-[11px]"
                style={{ color: "rgba(255,255,255,0.85)", letterSpacing: "0.2em" }}
              >
                Active warrant{cases.length === 1 ? "" : "s"} outstanding
              </div>
            </div>
            <span
              className="text-[12px] tabular"
              style={{ color: "rgba(255,255,255,0.85)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}
            >
              {cases.length} warrant{cases.length === 1 ? "" : "s"}
              {byCourt.length > 1 ? ` · ${byCourt.length} courts` : ""}
            </span>
          </div>

          {/* Headline */}
          <h2
            className="display mt-2"
            style={{
              fontSize: "clamp(22px, 2.4vw, 32px)",
              lineHeight: 1.1,
              fontVariationSettings: '"opsz" 96, "SOFT" 30',
              color: "#fff",
            }}
          >
            {cases.length === 1
              ? "There is an active bench warrant on file."
              : `There are ${cases.length} active bench warrants on file.`}
          </h2>

          <p
            className="mt-2"
            style={{
              color: "rgba(255,255,255,0.92)",
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: "clamp(13px, 1.1vw, 15px)",
              lineHeight: 1.5,
              maxWidth: 720,
            }}
          >
            Any traffic stop turns into a custody event while these are open.
            NJRC sends one warrant-recall letter per court, click a warrant
            below to see the charge, dates, and bail status. Each court has
            its own letter download.
          </p>

          {/* Per-court list */}
          <div className="mt-4 flex flex-col gap-3">
            {byCourt.map(([court, cs]) => {
              const head = cs[0];
              if (!head) return null;
              return (
                <div
                  key={court}
                  className="rounded-md p-3"
                  style={{
                    background: "rgba(0,0,0,0.18)",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                >
                  {/* Court header + per-court download */}
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span
                      style={{
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#fff",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {court}
                    </span>
                    <span
                      className="text-[11px]"
                      style={{
                        fontFamily: "var(--font-mono)",
                        color: "rgba(255,255,255,0.75)",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {cs.length} case{cs.length === 1 ? "" : "s"}
                    </span>
                    <a
                      href={`/api/jobs/${jobId}/letters/warrant.docx${
                        court !== "(unknown court)"
                          ? `?court=${encodeURIComponent(court)}`
                          : ""
                      }`}
                      className="rounded-md px-3 py-1.5 text-[12px] font-semibold transition hover:opacity-90 whitespace-nowrap"
                      style={{
                        background: "#fff",
                        color: "#6b1313",
                        fontFamily: "var(--font-mono)",
                        letterSpacing: "0.06em",
                        textDecoration: "none",
                      }}
                    >
                      ⤓ Warrant letter
                    </a>
                  </div>
                  {/* Court contact (address + tel:) */}
                  {(head.courtPhone || head.courtAddress) && (
                    <div
                      className="mt-1.5 text-[12px]"
                      style={{
                        fontFamily: "var(--font-mono)",
                        color: "rgba(255,255,255,0.75)",
                        letterSpacing: "0.03em",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {head.courtAddress && <span>{head.courtAddress}</span>}
                      {head.courtAddress && head.courtPhone && <span> · </span>}
                      {head.courtPhone && (
                        <a
                          href={`tel:${head.courtPhone.replace(/[^\d+]/g, "")}`}
                          style={{
                            color: "#fff",
                            textDecoration: "underline",
                            // Touch-target padding inline with the
                            // surrounding text - the .tel-link rule in
                            // index.css adds the minHeight/click box.
                          }}
                          className="tel-link"
                        >
                          {head.courtPhone}
                        </a>
                      )}
                    </div>
                  )}
                  {/* Per-warrant rows (click to expand) */}
                  <ul className="mt-2.5 space-y-2">
                    {cs.map((c) => (
                      <WarrantCaseRow key={c.recordId} c={c} />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function WarrantCaseRow({ c }: { c: WarrantCase }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const showWarrantNumber =
    c.warrantNumber && c.warrantNumber !== c.caseNumber;
  const hasDetail =
    c.warrantIssuedDate
    || c.originalOffenseDate
    || c.courtDateTime
    || c.courtRoom
    || c.status
    || showWarrantNumber;

  return (
    <li
      className="rounded-md overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.14)",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-baseline gap-2 flex-wrap p-2.5 transition hover:bg-white/[0.04]"
        style={{
          background: "transparent",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          color: "inherit",
          // overflow-wrap: anywhere lets long unbreakable tokens (case
          // numbers, offense codes) wrap mid-string on narrow viewports
          // instead of forcing horizontal scroll on the parent page.
          overflowWrap: "anywhere",
          minWidth: 0,
        }}
        aria-expanded={expanded}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 12,
            color: "rgba(255,255,255,0.85)",
            fontSize: 11,
            transition: "transform 0.15s ease",
            transform: expanded ? "rotate(90deg)" : "none",
            flexShrink: 0,
          }}
        >
          ▶
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "0.04em",
            overflowWrap: "anywhere",
            minWidth: 0,
          }}
        >
          {c.caseNumber || `Record #${c.recordId}`}
        </span>
        {c.offenseCode && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "rgba(255,255,255,0.7)",
              letterSpacing: "0.04em",
              overflowWrap: "anywhere",
              minWidth: 0,
            }}
          >
            · {c.offenseCode}
          </span>
        )}
        {c.offenseDescription && (
          <span
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: 13,
              color: "rgba(255,255,255,0.92)",
              // 220px basis forces the description to wrap to its
              // own line on narrow viewports rather than getting
              // squeezed into a 1-character-wide column beside the
              // bail badge.
              flex: "1 1 220px",
              minWidth: 0,
              marginLeft: 6,
              overflowWrap: "anywhere",
            }}
          >
            {c.offenseDescription}
          </span>
        )}
        {c.bail && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 4,
              background: "rgba(255,255,255,0.18)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.35)",
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {c.bail}
          </span>
        )}
      </button>
      {expanded && hasDetail && (
        <div
          className="px-3 pb-3 pt-1"
          style={{
            borderTop: "1px dashed rgba(255,255,255,0.18)",
            background: "rgba(0,0,0,0.12)",
          }}
        >
          <div
            className="flex flex-wrap pt-2"
            style={{
              fontFamily: "var(--font-mono)",
              color: "rgba(255,255,255,0.75)",
              letterSpacing: "0.03em",
              fontSize: 11,
              columnGap: 14,
              rowGap: 4,
            }}
          >
            {c.warrantIssuedDate && (
              <span>
                Warrant issued{" "}
                <span style={{ color: "#fff" }}>{c.warrantIssuedDate}</span>
              </span>
            )}
            {c.originalOffenseDate && (
              <span>
                Offense{" "}
                <span style={{ color: "#fff" }}>{c.originalOffenseDate}</span>
              </span>
            )}
            {c.courtDateTime && (
              <span>
                Last court date{" "}
                <span style={{ color: "#fff" }}>{c.courtDateTime}</span>
              </span>
            )}
            {c.courtRoom && (
              <span>
                Room <span style={{ color: "#fff" }}>{c.courtRoom}</span>
              </span>
            )}
            {c.status && (
              <span>
                Status <span style={{ color: "#fff" }}>{c.status}</span>
              </span>
            )}
            {showWarrantNumber && (
              <span>
                Warrant # <span style={{ color: "#fff" }}>{c.warrantNumber}</span>
              </span>
            )}
          </div>
          <a
            href={`#record-${c.recordId}`}
            className="mt-2 inline-block text-[11px] transition hover:opacity-100"
            style={{
              color: "rgba(255,255,255,0.85)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.06em",
              textDecoration: "underline",
              opacity: 0.8,
            }}
          >
            Jump to record card →
          </a>
        </div>
      )}
    </li>
  );
}
